// Identifiers [03 BR-DM-001; 02 §6.3/§6.5]. Entity ids are Postgres `bigint` identities, carried as
// decimal strings in JSON (no precision loss); request/outbox/idempotency ids are UUID v7.
import { z } from 'zod';

declare const idBrand: unique symbol;
declare const uuidBrand: unique symbol;

/** Entity id (bigint identity as a decimal string). `Id<'style'>` narrows to one entity kind. */
export type Id<Kind extends string = string> = string & { readonly [idBrand]: Kind };
/** UUID v7 (request id, outbox event id, idempotency key, trace correlation). */
export type UuidV7 = string & { readonly [uuidBrand]: 'v7' };

const ID_RE = /^[1-9][0-9]{0,18}$/;
const MAX_BIGINT = 9223372036854775807n;
const UUID_V7_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export function isId(value: unknown): value is Id {
  return typeof value === 'string' && ID_RE.test(value) && BigInt(value) <= MAX_BIGINT;
}

/** Validates and brands an entity id; accepts a safe integer, bigint or decimal string. */
export function toId<Kind extends string = string>(value: string | number | bigint): Id<Kind> {
  const text = typeof value === 'string' ? value : value.toString();
  if (typeof value === 'number' && !Number.isSafeInteger(value)) throw new RangeError('unsafe id');
  if (!isId(text)) throw new RangeError(`invalid id: ${text}`);
  return text as Id<Kind>;
}

export const idSchema = z
  .string()
  .refine(isId, { message: 'invalid id' })
  .transform((v) => v as Id);

export function isUuidV7(value: unknown): value is UuidV7 {
  return typeof value === 'string' && UUID_V7_RE.test(value);
}

export const uuidV7Schema = z
  .string()
  .refine(isUuidV7, { message: 'invalid uuid v7' })
  .transform((v) => v as UuidV7);

const HEX = '0123456789abcdef';

/**
 * Builds a UUID v7 (RFC 9562) from a Unix-ms timestamp and ≥ 10 random bytes (pure: callers inject
 * time and randomness through the Clock/Ids ports). Lexical order follows `unixMs`.
 */
export function uuidV7From(unixMs: number, random: Uint8Array): UuidV7 {
  if (!Number.isSafeInteger(unixMs) || unixMs < 0 || unixMs > 0xffffffffffff)
    throw new RangeError('unixMs out of range');
  if (random.length < 10) throw new RangeError('need at least 10 random bytes');
  const bytes = new Uint8Array(16);
  let ms = unixMs;
  for (let i = 5; i >= 0; i -= 1) {
    bytes[i] = ms % 256;
    ms = Math.floor(ms / 256);
  }
  for (let i = 6; i < 16; i += 1) bytes[i] = random[i - 6] ?? 0;
  bytes[6] = 0x70 | ((bytes[6] ?? 0) & 0x0f);
  bytes[8] = 0x80 | ((bytes[8] ?? 0) & 0x3f);
  let out = '';
  for (let i = 0; i < 16; i += 1) {
    if (i === 4 || i === 6 || i === 8 || i === 10) out += '-';
    const b = bytes[i] ?? 0;
    out += (HEX[b >> 4] ?? '') + (HEX[b & 15] ?? '');
  }
  return out as UuidV7;
}

/** Unix-ms timestamp embedded in a UUID v7. */
export function uuidV7Time(id: UuidV7): number {
  return Number.parseInt(id.slice(0, 8) + id.slice(9, 13), 16);
}
